/**
 * Authentication Hook
 * Provides React integration for the centralized AuthService
 * 
 * This hook provides:
 * - Reactive authentication state
 * - Permission checking
 * - Login/logout functionality
 * - Automatic re-renders on auth changes
 */

import { useState, useEffect, useCallback } from 'react';
import authService from '../services/AuthService';
import { USER_ROLES } from '../constants/appConfig';

export const useAuth = () => {
  const [user, setUser] = useState(authService.getUser());
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update state when auth changes
  useEffect(() => {
    const unsubscribe = authService.addListener((event, data) => {
      switch (event) {
        case 'login':
        case 'tokenRefresh':
          setUser(data);
          setIsAuthenticated(true);
          setError(null);
          break;
        case 'logout':
          setUser(null);
          setIsAuthenticated(false);
          setError(null);
          break;
        case 'authError':
          setError(data.message || 'Authentication error');
          setUser(null);
          setIsAuthenticated(false);
          break;
        default:
          break;
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Login function
  const login = useCallback(async (credentials) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await authService.login(credentials);
      return result;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Permission checking
  const hasPermission = useCallback((permission) => {
    return authService.hasPermission(permission);
  }, []);

  // Role checking
  const hasRole = useCallback((role) => {
    return authService.hasRole(role);
  }, []);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return hasRole(USER_ROLES.SUPER_ADMIN) || hasRole(USER_ROLES.ADMIN);
  }, [hasRole]);

  // Check if user is corporate admin
  const isCorporateAdmin = useCallback(() => {
    return hasRole(USER_ROLES.CORPORATE_ADMIN);
  }, [hasRole]);

  // Get user's corporate ID
  const getCorporateId = useCallback(() => {
    return authService.getCorporateId();
  }, []);

  // Get user's email
  const getUserEmail = useCallback(() => {
    return authService.getUserEmail();
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    
    // Actions
    login,
    logout,
    clearError,
    
    // Utilities
    hasPermission,
    hasRole,
    isAdmin,
    isCorporateAdmin,
    getCorporateId,
    getUserEmail,
    
    // User properties (for convenience)
    userRole: user?.roleID,
    userName: user?.name || user?.firstName,
    userEmail: user?.emailID,
    corporateId: user?.corporateID
  };
};

/**
 * Hook for protecting routes based on permissions
 */
export const usePermissionGuard = (requiredPermission) => {
  const { hasPermission, isAuthenticated } = useAuth();
  
  const canAccess = isAuthenticated && hasPermission(requiredPermission);
  
  return {
    canAccess,
    isAuthenticated,
    hasPermission: hasPermission(requiredPermission)
  };
};

/**
 * Hook for protecting routes based on roles
 */
export const useRoleGuard = (requiredRoles) => {
  const { hasRole, isAuthenticated, userRole } = useAuth();
  
  const rolesArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  const canAccess = isAuthenticated && rolesArray.some(role => hasRole(role));
  
  return {
    canAccess,
    isAuthenticated,
    userRole,
    hasRequiredRole: canAccess
  };
};

/**
 * Hook for components that need user data
 */
export const useUserData = () => {
  const { user, isAuthenticated } = useAuth();
  
  return {
    user,
    isAuthenticated,
    // Commonly used user properties
    userId: user?.id || user?.userID,
    userName: user?.name || user?.firstName,
    userEmail: user?.emailID,
    userRole: user?.roleID,
    corporateId: user?.corporateID,
    departmentId: user?.departmentID,
    // User status checks
    isActive: user?.status === 'active',
    isVerified: user?.isVerified === true
  };
};

/**
 * Higher-order component for authentication protection
 */
export const withAuth = (WrappedComponent, options = {}) => {
  const { 
    requiredPermission, 
    requiredRole, 
    redirectTo = '/login',
    fallback = null 
  } = options;
  
  return function AuthProtectedComponent(props) {
    const auth = useAuth();
    
    if (!auth.isAuthenticated) {
      if (fallback) {
        return fallback;
      }
      // In a real app, you'd use React Router to redirect
      window.location.href = redirectTo;
      return null;
    }
    
    if (requiredPermission && !auth.hasPermission(requiredPermission)) {
      return fallback || <div>Access Denied</div>;
    }
    
    if (requiredRole && !auth.hasRole(requiredRole)) {
      return fallback || <div>Access Denied</div>;
    }
    
    return <WrappedComponent {...props} auth={auth} />;
  };
};

export default useAuth;
